'use client';
import type { Analysis, Workout } from '@trainme/db/client';
import type { WorkoutWithRelations } from '@/utils/types';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { FiLock, FiPlus, FiSave, FiUnlock, FiWatch } from 'react-icons/fi';
import { TimePeriod, WorkoutStatus } from '@/utils/prismaEnums';
import { WorkoutState, useWorkoutStore } from '@/app/stores/workoutStore';
import {
  defaultWorkout,
  workoutStepsInputPlaceholder,
  defaultSettings,
  calculateStrengthDurationSeconds,
  workoutTotalDistance,
  workoutTotalDuration,
} from '@trainme/api/client';
import { cn } from '@/utils/uiUtils';
import { endOfWeek, startOfDay, startOfWeek } from 'date-fns';
import { formatDistance, metersToKilometers } from '@trainme/api/client';
import { secondsToHm, secondsToMinutes } from '@/utils/timeUtils';
import SportTypeSelect from '@/app/components/SportTypeSelect';
import { TipsWorkoutSteps } from '@/app/components/UsageTips';
import { WorkoutChart } from './WorkoutChart';
import StrengthWorkoutComposer from './StrengthWorkoutComposer';
import { toast } from 'react-toastify';
import { trpc } from '@/app/api/trpc/client';
import { useAnalysisStore } from '@/app/stores/analysisStore';
import { useCalendar } from '@/app/calendar/utils/useCalendar';
import { useCalendarState } from '@/app/calendar/utils/useCalendarState';
import { useGarminSyncButton } from '@/app/workouts/hooks/useGarminSyncButton';
import { useSettingsStore } from '@/app/stores/settingsStore';
import { CoachSayCard } from '@/app/components/CoachSayCard';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';
import {
  getDefaultStrengthWeightUnit,
  readStrengthWeightUnitCookie,
  unitToLabel,
} from './strengthWeightUnit';
import { useRef } from 'react';
import SegmentedToggle from '@/app/components/ui/SegmentedToggle';

type WorkoutEditorProps = {
  onSave?: () => void;
  onDelete?: () => void;
  className?: string;
  onClose?: () => void;
  hideFooter?: boolean;
  onAnalysisReady?: (hasAnalysis: boolean) => void;
  onAnalysisTriggerStateChange?: (state: {
    canTriggerAnalysis: boolean;
    triggerAnalysis: () => void;
    isTriggeringAnalysis: boolean;
  }) => void;
  onGarminSyncStateChange?: (state: {
    canSync: boolean;
    handleGarminAction: () => void;
    isGarminActionLoading: boolean;
    buttonTitle: string;
  }) => void;
};

type WorkoutEditorInnerProps = WorkoutEditorProps & {
  workout: WorkoutWithRelations;
  scheduleDate: Date;
  workouts: WorkoutWithRelations[];
  setWorkout: (workout: WorkoutWithRelations | null) => void;
  onAnalysisTriggerStateChange?: (state: {
    canTriggerAnalysis: boolean;
    triggerAnalysis: () => void;
    isTriggeringAnalysis: boolean;
  }) => void;
  onGarminSyncStateChange?: (state: {
    canSync: boolean;
    handleGarminAction: () => void;
    isGarminActionLoading: boolean;
    buttonTitle: string;
  }) => void;
};

export function WorkoutEditor(props: WorkoutEditorProps) {
  const { workout, scheduleDate, workouts, setWorkout } = useCalendarState();
  if (!workout) return <></>;
  return (
    <WorkoutEditorInner
      key={workout.id || 'new'}
      workout={workout}
      scheduleDate={scheduleDate}
      workouts={workouts}
      setWorkout={setWorkout}
      onAnalysisReady={props.onAnalysisReady}
      onAnalysisTriggerStateChange={props.onAnalysisTriggerStateChange}
      onGarminSyncStateChange={props.onGarminSyncStateChange}
      {...props}
    />
  );
}

const formatStepsText = (steps: Workout['steps']) =>
  Array.isArray(steps)
    ? steps.map((step) => `${step}`).join('\n')
    : steps?.toString() ?? '';

const getDefaultStrengthStep = () =>
  `1x(Chest: Bench Press x 12 @ 25 ${unitToLabel(
    readStrengthWeightUnitCookie() ?? getDefaultStrengthWeightUnit()
  )} + rest 1 min)`;

const DEFAULT_STEP = '10min Z2 easy';

function WorkoutEditorInner({
  onSave,
  onDelete,
  className,
  onClose,
  hideFooter,
  onAnalysisReady,
  workout,
  scheduleDate,
  setWorkout,
  workouts,
  onAnalysisTriggerStateChange,
  onGarminSyncStateChange,
}: WorkoutEditorInnerProps & { hideFooter?: boolean }) {
  const { mergeAnalyses } = useAnalysisStore();
  const { loadWeekData } = useCalendar();
  const utils = trpc.useUtils();
  const getSportsSpeeds = useSettingsStore((state) => state.getSportsSpeeds);
  const unitSystem = useUnitSystem();
  const { control, setValue, handleSubmit } = useForm<Workout>({
    defaultValues: workout,
    mode: 'onChange',
  });
  const stepsTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isStrengthWorkout = workout.sportType === 'WeightTraining';

  const { mutate: upsertWorkout } = trpc.workouts.upsert.useMutation({
    onSuccess: async (response) => {
      if (!response.success) {
        const errorMessage =
          'error' in response ? response.error : 'Unknown error';
        toast.error('Failed to create workout: ' + errorMessage);
        console.error('Failed to upsert workout:', errorMessage);
        return;
      }
      toast.success('Workout added');
      onSave?.();

      // Add the workout directly to the Zustand store for immediate UI update
      if (response?.data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mergeWorkouts([response.data as any]);
      }

      // Add the analyses from auto-match to the store (already in DB, just update store)
      if (
        response &&
        'analyses' in response &&
        Array.isArray(response.analyses) &&
        (response.analyses as Analysis[]).length > 0
      ) {
        mergeAnalyses(response.analyses as Analysis[]);
      }

      // Invalidate cache for future loads
      utils.workouts.getMany.invalidate();
      utils.analysis.getMany.invalidate();

      // Reload the affected date's week without showing loading indicators
      if (response?.data?.date) {
        const workoutDate =
          response.data.date instanceof Date
            ? response.data.date
            : new Date(response.data.date);
        const weekStart = startOfWeek(workoutDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(workoutDate, { weekStartsOn: 1 });
        await loadWeekData({ start: weekStart, end: weekEnd }, true);

        // Invalidate and refetch timely analysis queries for the affected day and week
        const normalizedDate = startOfDay(workoutDate);
        const normalizedWeekStart = startOfDay(weekStart);
        await Promise.all([
          utils.analysis.getTimelyAnalysis.invalidate({
            period: TimePeriod.DAILY,
            startDate: normalizedDate,
          }),
          utils.analysis.getTimelyAnalysis.invalidate({
            period: TimePeriod.WEEKLY,
            startDate: normalizedWeekStart,
          }),
        ]);
        // Explicitly fetch to ensure UI updates
        await Promise.all([
          utils.analysis.getTimelyAnalysis.fetch({
            period: TimePeriod.DAILY,
            startDate: normalizedDate,
          }),
          utils.analysis.getTimelyAnalysis.fetch({
            period: TimePeriod.WEEKLY,
            startDate: normalizedWeekStart,
          }),
        ]);
      }
    },
    onError: (error) => {
      toast.error('Failed to create workout: ' + error);
    },
  });

  const mergeWorkouts = useWorkoutStore(
    (state: WorkoutState) => state.mergeWorkouts
  );

  const [stepsText, setStepsText] = useState(() =>
    workout.sportType === 'WeightTraining'
      ? formatStepsText(workout.steps ?? [getDefaultStrengthStep()])
      : formatStepsText(workout.steps ?? defaultWorkout.steps)
  );
  const [strengthComposerMode, setStrengthComposerMode] = useState<
    'text' | 'form'
  >('form');

  useEffect(() => {
    const textarea = stepsTextareaRef.current;
    if (!textarea || workout.sportType === 'WeightTraining') return;

    textarea.style.height = 'auto';
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(computedStyle.lineHeight);
    const verticalPadding =
      Number.parseFloat(computedStyle.paddingTop) +
      Number.parseFloat(computedStyle.paddingBottom);
    const minHeight = lineHeight * 5 + verticalPadding;
    const maxHeight = lineHeight * 10 + verticalPadding;
    const nextHeight = Math.min(
      Math.max(textarea.scrollHeight, minHeight),
      maxHeight
    );
    textarea.style.height = `${nextHeight}px`;
  }, [stepsText, workout.sportType]);

  const updateWorkout = useCallback(
    (updates: Partial<Workout>, markEdited: boolean = false) => {
      const nextIsUserEdited = markEdited ? true : workout.isUserEdited;
      setWorkout({ ...workout, ...updates, isUserEdited: nextIsUserEdited });
      if (markEdited && !workout.isUserEdited) {
        setValue('isUserEdited', true, { shouldDirty: true });
      }
    },
    [setWorkout, setValue, workout]
  );

  const {
    isLoading: isGarminActionLoading,
    handleGarminAction,
    buttonTitle,
    buttonColorClasses,
  } = useGarminSyncButton({
    workout,
    onWorkoutUpdate: (updatedWorkout) => {
      mergeWorkouts([updatedWorkout]);
      setWorkout(updatedWorkout);
      setStepsText(formatStepsText(updatedWorkout.steps));
      updateWorkout(
        {
          garminWorkoutId: updatedWorkout.garminWorkoutId,
          garminScheduleId: updatedWorkout.garminScheduleId,
        },
        false
      );
    },
  });

  const isLocked = Boolean(workout.isUserEdited);
  const handleToggleLock = () => {
    if (!workout) return;
    updateWorkout({ isUserEdited: !workout.isUserEdited }, true);
  };

  const analysisQuery = trpc.analysis.getWorkoutAnalysis.useQuery(
    { workoutId: workout.id },
    {
      enabled: Boolean(workout.id),
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: (query) => {
        const state = query.state.data?.analysisState;
        return state === 'analyzing' ? 3000 : false;
      },
    }
  );

  const analysis = analysisQuery.data?.analysis;
  const analysisState = analysisQuery.data?.analysisState ?? 'pending';
  const hasAnalysisScore =
    typeof analysis?.trainmeScore === 'number' &&
    Number.isFinite(analysis.trainmeScore);

  const { mutate: retriggerAnalysis, isPending: isRetriggeringAnalysis } =
    trpc.workouts.retriggerAnalysis.useMutation({
      onSuccess: async (response) => {
        const candidateCount = response?.matchCount || 0;

        if (response?.queued || response?.alreadyQueued) {
          await analysisQuery.refetch();
          toast.info(
            `Analysis queued for ${candidateCount} ${
              candidateCount === 1 ? 'activity' : 'activities'
            }.`
          );
          return;
        }

        if (candidateCount === 0) {
          toast.info('No matching activities found for analysis.');
        } else {
          toast.info(
            `Analysis request skipped for ${candidateCount} ${
              candidateCount === 1 ? 'activity' : 'activities'
            }.`
          );
        }
      },
      onError: () => {
        toast.error('Failed to analyze activities.');
      },
    });

  const canShowAnalyzeButton =
    Boolean(workout.id) &&
    !analysis &&
    workout.status === WorkoutStatus.PENDING;

  const canTriggerAnalysis = canShowAnalyzeButton;
  const triggerAnalysis = useCallback(() => {
    if (!canTriggerAnalysis || isRetriggeringAnalysis) {
      return;
    }
    retriggerAnalysis({ workoutId: workout.id });
  }, [
    canTriggerAnalysis,
    isRetriggeringAnalysis,
    retriggerAnalysis,
    workout.id,
  ]);

  const isWorkoutPending =
    workout.status === WorkoutStatus.PENDING && !analysis;

  useEffect(() => {
    onAnalysisTriggerStateChange?.({
      canTriggerAnalysis,
      triggerAnalysis,
      isTriggeringAnalysis:
        isRetriggeringAnalysis || analysisState === 'analyzing',
    });
  }, [
    canTriggerAnalysis,
    triggerAnalysis,
    isRetriggeringAnalysis,
    analysisState,
    onAnalysisTriggerStateChange,
  ]);

  useEffect(() => {
    onGarminSyncStateChange?.({
      canSync: Boolean(workout.id),
      handleGarminAction,
      isGarminActionLoading,
      buttonTitle,
    });
  }, [
    onGarminSyncStateChange,
    workout.id,
    handleGarminAction,
    isGarminActionLoading,
    buttonTitle,
  ]);

  useEffect(() => {
    if (analysis && workout.status !== WorkoutStatus.COMPLETED && workout.id) {
      const normalizedWorkout = {
        ...workout,
        status: WorkoutStatus.COMPLETED,
      };
      mergeWorkouts([normalizedWorkout]);
      setWorkout(normalizedWorkout);
    }
  }, [analysis, workout, mergeWorkouts, setWorkout]);

  useEffect(() => {
    onAnalysisReady?.(Boolean(analysis));
  }, [analysis, onAnalysisReady]);

  const handleStepsChange = useCallback(
    (value: string) => {
      const parsedSteps = value
        .split('\n')
        .map((step) => step.replace(/^- /, ''))
        .map((step) => step.replace(/(\s+)/, ' '))
        .filter((step) => step.trim().length > 0);
      const sportType = workout.sportType || 'Run';
      const currentSportsSpeeds = getSportsSpeeds();
      const resolvedSportsSpeeds =
        currentSportsSpeeds ?? defaultSettings.sportsSpeeds;
      const totalDistance = metersToKilometers(
        isStrengthWorkout
          ? 0
          : workoutTotalDistance(parsedSteps, sportType, resolvedSportsSpeeds)
      );

      const totalDuration = secondsToMinutes(
        isStrengthWorkout
          ? calculateStrengthDurationSeconds(parsedSteps)
          : workoutTotalDuration(parsedSteps, sportType, resolvedSportsSpeeds)
      );

      const updatedWorkout = {
        steps: parsedSteps,
        distance: totalDistance,
        duration: totalDuration,
      };

      // Update form values
      setValue('steps', parsedSteps);
      setValue('distance', totalDistance);
      setValue('duration', totalDuration);

      updateWorkout(updatedWorkout, true);
      return parsedSteps;
    },
    [getSportsSpeeds, updateWorkout, setValue, workout, isStrengthWorkout]
  );

  const handleAddStrengthStep = useCallback(() => {
    const defaultStrengthStep = getDefaultStrengthStep();
    if (strengthComposerMode === 'text') {
      const nextSteps = [
        ...((Array.isArray(workout.steps) ? workout.steps : []) as string[]),
        defaultStrengthStep,
      ];
      setStepsText(nextSteps.join('\n'));
      handleStepsChange(nextSteps.join('\n'));
      return;
    }

    const nextSteps = [
      ...((Array.isArray(workout.steps) ? workout.steps : []) as string[]),
      defaultStrengthStep,
    ];
    setStepsText(nextSteps.join('\n'));
    handleStepsChange(nextSteps.join('\n'));
  }, [handleStepsChange, strengthComposerMode, workout.steps]);

  const onSubmit = (data: Workout) => {
    const resolvedDate = data.date ?? workout.date ?? scheduleDate;
    upsertWorkout({
      workout: {
        ...data,
        isUserEdited: workout.isUserEdited,
        date: resolvedDate,
      },
    });
  };

  return (
    <div
      className={cn([
        'h-full min-h-0 w-full',
        'flex flex-col justify-start',
        'text-gray-700 dark:text-gray-100 rounded',
        className,
      ])}
    >
      <form
        id='workout-editor-form'
        className='flex min-h-0 flex-1 flex-col'
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className='flex flex-col gap-2 p-3 pb-0'>
          <div
            className={cn([
              'grid gap-4 items-stretch auto-rows-max',
              hasAnalysisScore
                ? 'lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.85fr)]'
                : 'grid-cols-1',
            ])}
          >
            <div
              className={cn([
                'flex flex-col gap-4',
                hasAnalysisScore ? 'pr-2' : '',
              ])}
            >
              <div className='grid gap-2 text-[0.9em]'>
                <div className='grid gap-2 md:grid-cols-3'>
                  <div className='col-span-2 flex flex-col gap-1'>
                    <label
                      htmlFor='name'
                      className='text-xs uppercase tracking-[0.2em] text-slate-300 dark:text-slate-400'
                    >
                      Workout Name
                    </label>
                    <Controller
                      name='name'
                      control={control}
                      rules={{
                        validate: {
                          notTaken: (value) =>
                            workouts
                              ?.map((workout: Workout) => workout.name)
                              .includes(value?.toString().trim() ?? '')
                              ? 'Name taken'
                              : true,
                        },
                        required: 'Workout name is required',
                      }}
                      render={({ field, fieldState }) => (
                        <div>
                          <input
                            id='name'
                            className='block w-full px-4 h-9 text-[0.9em] font-sans rounded-lg bg-stone-300 dark:bg-stone-700 text-gray-900 dark:text-gray-100 border border-stone-400 dark:border-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-500/40 box-border'
                            placeholder='Workout Name'
                            value={field.value?.toString() ?? ''}
                            onChange={(e) => {
                              field.onChange(e);
                              updateWorkout(
                                { name: e.target.value.trim() },
                                true
                              );
                            }}
                            onBlur={() => {
                              field.onBlur();
                            }}
                          />
                          {fieldState.error && (
                            <span className='text-red-500'>
                              {fieldState.error.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                  <div className='flex flex-col gap-1'>
                    <label
                      htmlFor='type'
                      className='text-xs uppercase tracking-[0.2em] text-slate-300 dark:text-slate-400'
                    >
                      Sport Type
                    </label>
                    <Controller
                      name='sportType'
                      control={control}
                      rules={{ required: 'Sport type is required' }}
                      render={({ field }) => (
                        <SportTypeSelect
                          value={field.value ?? ''}
                          onChange={(selectedSport) => {
                            field.onChange(selectedSport);
                            const steps = Array.isArray(workout.steps)
                              ? (workout.steps as string[])
                              : [];
                            const updates: Partial<Workout> = {
                              sportType: selectedSport,
                            };
                            if (steps.length > 0) {
                              const sportsSpeeds = getSportsSpeeds();
                              const totalDistance = workoutTotalDistance(
                                steps,
                                selectedSport,
                                sportsSpeeds
                              );
                              const totalDuration = workoutTotalDuration(
                                steps,
                                selectedSport,
                                sportsSpeeds
                              );
                              updates.distance =
                                metersToKilometers(totalDistance);
                              updates.duration =
                                selectedSport === 'WeightTraining'
                                  ? secondsToMinutes(
                                      calculateStrengthDurationSeconds(steps)
                                    )
                                  : secondsToMinutes(totalDuration);
                              setValue(
                                'distance',
                                metersToKilometers(totalDistance)
                              );
                              setValue(
                                'duration',
                                selectedSport === 'WeightTraining'
                                  ? secondsToMinutes(
                                      calculateStrengthDurationSeconds(steps)
                                    )
                                  : secondsToMinutes(totalDuration)
                              );
                            }
                            updateWorkout(updates, true);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className='flex flex-col gap-1'>
                  <label className='text-xs uppercase tracking-[0.2em] text-slate-300 dark:text-slate-400'>
                    Coach's Notes
                  </label>
                  {workout.description ? (
                    <p className='whitespace-pre-wrap rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm leading-relaxed text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'>
                      {workout.description}
                    </p>
                  ) : null}
                </div>
                <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
                  <div className='flex items-center gap-2 text-sm font-medium'>
                    <span className='text-xs uppercase tracking-[0.2em] text-slate-300 dark:text-slate-400'>
                      Distance:
                    </span>
                    <span className='rounded-lg bg-black/10 px-3 py-1 dark:bg-white/5'>
                      {workout.distance
                        ? formatDistance(workout.distance * 1000, unitSystem)
                        : null}
                    </span>
                  </div>
                  <div className='flex items-center gap-2 text-sm font-medium'>
                    <span className='text-xs uppercase tracking-[0.2em] text-slate-300 dark:text-slate-400'>
                      Duration:
                    </span>
                    <span className='rounded-lg bg-black/10 px-3 py-1 dark:bg-white/5'>
                      {secondsToHm((workout.duration || 0) * 60)}
                    </span>
                  </div>
                  {isStrengthWorkout ? (
                    <div className='ml-auto flex items-center gap-2'>
                      <button
                        type='button'
                        className='inline-flex h-[15px] min-h-[15px] items-center justify-center gap-1 rounded-full border border-current px-2 py-0 text-[7px] font-bold uppercase tracking-wider leading-none box-border text-[#5f6875] transition-colors hover:bg-white/20 dark:text-white'
                        onClick={handleAddStrengthStep}
                        aria-label='Add new step'
                        title='Add'
                      >
                        <FiPlus size={9} />
                        <span className='sr-only sm:not-sr-only text-[7px] leading-none'>
                          Add Step
                        </span>
                      </button>
                      <SegmentedToggle
                        value={strengthComposerMode}
                        options={[
                          { value: 'text', label: 'Text' },
                          { value: 'form', label: 'Form' },
                        ]}
                        onChange={(next) => setStrengthComposerMode(next)}
                        size='xs'
                      />
                    </div>
                  ) : (
                    <div />
                  )}
                </div>
                <div className='form-group items-start hidden'>
                  <label htmlFor='status'>Status</label>
                  <Controller
                    name='status'
                    control={control}
                    render={({ field }) => (
                      <select
                        id='status'
                        className='px-4 text-[0.9em] bg-white dark:bg-slate-800 rounded border border-gray-300 dark:border-gray-600 h-10 w-full'
                        value={field.value ?? WorkoutStatus.PENDING}
                        onChange={(e) => {
                          const newStatus = e.target.value as WorkoutStatus;
                          field.onChange(newStatus);
                          updateWorkout({ status: newStatus }, true);
                        }}
                      >
                        <option value={WorkoutStatus.PENDING}>Pending</option>
                        <option value={WorkoutStatus.COMPLETED}>
                          Completed
                        </option>
                        <option value={WorkoutStatus.SKIPPED}>Skipped</option>
                      </select>
                    )}
                  />
                </div>
                <div className='form-group items-start hidden'>
                  <label htmlFor='workout-notes'>My Notes</label>
                  <Controller
                    name='notes'
                    control={control}
                    render={({ field }) => (
                      <div>
                        <textarea
                          id='workout-notes'
                          className='px-4 h-fit min-h-24 overflow-y-auto scroll'
                          placeholder='Workout Notes'
                          value={field.value ?? ''}
                          onChange={(e) => {
                            field.onChange(e);
                            updateWorkout({ notes: e.target.value }, true);
                          }}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className='relative w-full'>
                {isStrengthWorkout ? (
                  <Controller
                    name='steps'
                    control={control}
                    render={({ field }) => (
                      <div className='flex flex-col'>
                        <StrengthWorkoutComposer
                          key={`${workout.id || 'new'}-${workout.sportType || 'unknown'}`}
                          value={(field.value as string[] | undefined) ?? []}
                          onChange={(next) => {
                            field.onChange(next);
                            setStepsText(next.join('\n'));
                            handleStepsChange(next.join('\n'));
                          }}
                          showToolbar={false}
                          mode={strengthComposerMode}
                          onModeChange={setStrengthComposerMode}
                        />
                      </div>
                    )}
                  />
                ) : (
                  <Controller
                    name='steps'
                    control={control}
                    render={({ field }) => {
                      return (
                        <textarea
                          id='steps'
                          className={cn(
                            'workout-board h-full min-h-[220px] w-full resize-none p-4 text-sm font-sans overflow-y-auto'
                          )}
                          ref={stepsTextareaRef}
                          value={stepsText}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setStepsText(nextValue);
                            const normalizedSteps =
                              handleStepsChange(nextValue);
                            field.onChange(normalizedSteps);
                          }}
                          onFocus={() => {
                            if (stepsText) return;
                            setStepsText(DEFAULT_STEP);
                            const normalizedSteps =
                              handleStepsChange(DEFAULT_STEP);
                            field.onChange(normalizedSteps);
                          }}
                          placeholder={workoutStepsInputPlaceholder}
                        />
                      );
                    }}
                  />
                )}
              </div>
              <div className='mt-auto min-h-[160px] w-full min-w-0 saturate-150'>
                {isStrengthWorkout ? (
                  <WorkoutChart
                    key={`strength-${workout.id}-${stepsText}`}
                    createKey='editor-strength'
                    workout={workout}
                    workoutStepsOverride={stepsText.split('\n')}
                  />
                ) : (
                  <WorkoutChart createKey='editor' workout={workout} />
                )}
              </div>
            </div>
            {hasAnalysisScore && (
              <div className='min-h-0 min-w-0 self-stretch overflow-y-auto pb-4 pl-0 lg:pl-2'>
                <CoachSayCard
                  coachSay={analysis?.coachSay}
                  trainmeScore={analysis?.trainmeScore}
                  coachMessageId={analysis?.coachMessageId}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer Area */}
        {!hideFooter && (
          <div className='mt-auto shrink-0 border-t border-gray-100 bg-white px-3 py-1.5 dark:border-gray-800 dark:bg-slate-900'>
            <div className='flex items-center justify-between gap-4'>
              <div className='flex items-center gap-2'>
                <TipsWorkoutSteps placement='bottom-end' />
                <button
                  type='button'
                  className={cn(
                    'inline-flex items-center gap-1 border border-current rounded-lg py-1 px-2 text-sm mr-3 transition-opacity',
                    buttonColorClasses,
                    (!workout.id || isGarminActionLoading) &&
                      'cursor-not-allowed opacity-50',
                    isGarminActionLoading && 'cursor-wait opacity-80'
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleGarminAction();
                  }}
                  disabled={!workout.id || isGarminActionLoading}
                  title={buttonTitle}
                >
                  <FiWatch
                    size={16}
                    className={cn(isGarminActionLoading && 'animate-spin')}
                  />
                  <span>{buttonTitle}</span>
                </button>
              </div>

              <div className='flex items-center gap-3'>
                {onDelete && (
                  <button
                    type='button'
                    className='btn btn-danger'
                    onClick={onDelete}
                  >
                    Delete
                  </button>
                )}

                {onClose && (
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                )}

                <button
                  type='button'
                  className='btn btn-secondary p-1 text-gray-800 dark:text-gray-100 hover:opacity-70 transition-opacity'
                  onClick={handleToggleLock}
                  aria-label={isLocked ? 'Unlock workout' : 'Lock workout'}
                  title={
                    isLocked
                      ? 'Unlock workout to allow AI Coach'
                      : 'Lock workout to prevent AI Coach from analyzing'
                  }
                >
                  {isLocked ? <FiUnlock size={16} /> : <FiLock size={16} />}
                </button>
                <button type='submit' className='btn btn-primary'>
                  {workout.id ? (
                    <>
                      <FiSave size={14} />
                      Update
                    </>
                  ) : (
                    <>
                      <FiPlus size={14} />
                      Create
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
